import { OctagonAlert, Rocket, SlidersHorizontal } from 'lucide-react'
import { Button } from '@/components/ui/button.tsx'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import FormField from '@/components/ui/form-field.tsx'
import { useIsUserSuperAdmin } from '@/stores/useStore.js'
import { ABorderBox } from '@/components/atoms/ABorderBox.tsx'
import { generateFirebaseId, COLLECTIONS } from '@/services/Firebase.ts'
import { useNavigate } from 'react-router-dom'
import ALogoSpinner from '@/components/atoms/ALogoSpinner.tsx'
import { Checkbox } from '@/components/ui/checkbox.tsx'
import { useCallback, useState, useEffect } from 'react'
import * as Upload from '@/components/molecules/Upload.tsx'
import { useBaseProjectPayload } from './hooks.ts'
import { useCreateProjectMutation } from './hooks.ts'
import {
  promptSchema,
  PromptData,
  extraPromptFeaturesSchema,
  ExtraPromptFeatures,
} from './schema.ts'
import { TemplateSelector } from './TemplateSelector.tsx'
import { useVMsUsage } from '@/lib/organizations/useVMsUsage.ts'
import { prompts } from './prompts.ts'
import { USECASE_ID } from '@/const/stackTemplates.ts'
import { useFeatureFlagEnabled } from 'posthog-js/react'
import * as Popover from '@/components/ui/popover.tsx'

const schema = promptSchema.merge(extraPromptFeaturesSchema)

export function MProjectPrompt() {
  const navigate = useNavigate()
  const isSuperAdmin = useIsUserSuperAdmin()
  const { reachedLimit } = useVMsUsage()

  const { organizationId, teamId } = useBaseProjectPayload()

  const isUploadEnabled = useFeatureFlagEnabled('prompt-upload')
  const isFormDisabled = useFeatureFlagEnabled('prompt-form-disabled')

  const form = useForm<PromptData & ExtraPromptFeatures>({
    resolver: zodResolver(schema),
    reValidateMode: 'onChange',
    defaultValues: {
      prompt: '',
      usecaseId: USECASE_ID.CUSTOM,
      organizationId,
      teamId,
      assets: [],
      researchEnabled: false,
      expandFrontendPrototype: false,
    },
  })

  useEffect(() => {
    form.setValue('organizationId', organizationId)
    form.setValue('teamId', teamId)
  }, [organizationId, teamId, form])

  const isValid = form.formState.isValid

  const usecaseId = form.watch('usecaseId')

  const mutation = useCreateProjectMutation(usecaseId)

  const onSubmit = async (data: PromptData & ExtraPromptFeatures) => {
    const projectId = generateFirebaseId(COLLECTIONS.projects)
    const iterationId = generateFirebaseId(COLLECTIONS.iterations)

    mutation.mutate({
      ...data,
      projectId,
      iterationId,
    })
    navigate(`/projects/${projectId}?iteration=${iterationId}`)
  }

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault()
        if (isValid && !mutation.isPending) {
          form.handleSubmit(onSubmit)()
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isValid, mutation.isPending, form.handleSubmit, onSubmit])

  const [isFocused, setIsFocused] = useState(false)

  const data = form.watch()

  const researchEnabled = form.watch('researchEnabled')
  const expandFrontendPrototype = form.watch('expandFrontendPrototype')

  if (isFormDisabled) {
    return (
      <ABorderBox isEnabled borderClasname="text-[#EEAA67]" className="rounded-md bg-white p-4">
        <div className="flex flex-col gap-4 text-center">
          <h2 className="text-2xl font-bold tracking-tight">
            Due to high demand, we&apos;re not accepting new projects at the moment.
          </h2>
          <p className="text-sm text-muted-foreground">
            Please check back later or contact us for more information. We&apos;re working on it!
          </p>
        </div>
      </ABorderBox>
    )
  }

  return (
    <Upload.Provider organizationId={organizationId}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-pp-supply-sans text-3xl font-bold tracking-tight">
            What do you want to build today?
          </h2>

          {isSuperAdmin && (
            <Popover.Popover>
              <Popover.PopoverTrigger asChild>
                <Button variant="ghost" className="ml-auto" size="icon">
                  <SlidersHorizontal className="size-4" />
                </Button>
              </Popover.PopoverTrigger>
              <Popover.PopoverContent side="bottom" align="end">
                <h4 className="mb-4 font-pp-supply-sans text-lg font-semibold">Settings</h4>
                <label className="flex items-center gap-2">
                  <Checkbox
                    checked={researchEnabled}
                    onCheckedChange={() => form.setValue('researchEnabled', !researchEnabled)}
                  />
                  <span>Use research</span>
                </label>
                <label className="flex items-center gap-2">
                  <Checkbox
                    checked={expandFrontendPrototype}
                    onCheckedChange={() =>
                      form.setValue('expandFrontendPrototype', !expandFrontendPrototype)
                    }
                  />
                  <span>Expand frontend prototype</span>
                </label>
              </Popover.PopoverContent>
            </Popover.Popover>
          )}
        </div>
        <ABorderBox
          isEnabled={mutation.isPending || isFocused}
          borderClasname="text-[#EEAA67]"
          className="rounded-md bg-white"
        >
          {reachedLimit && (
            <div className="mb-4 flex items-center justify-start gap-4 rounded-t-md bg-destructive/15 px-4 py-3 text-sm text-destructive">
              <OctagonAlert className="size-4" />
              <span>
                You hit active projects limit. Stop active project or contact us to upgrade your
                plan.
              </span>
            </div>
          )}
          <FormField.Root control={form.control} name="prompt" id={TEXTAREA_ID}>
            <FormField.Textarea
              {...form.register('prompt')}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              autogrow={true}
              variant="ghost"
              className="resize-none border-transparent bg-white shadow-none focus-visible:ring-0"
              placeholder="Describe your project..."
              disabled={reachedLimit}
            />
            <FormField.Error />
          </FormField.Root>

          <div className="px-4">
            <Upload.Previews />
          </div>
          <div className="flex items-center justify-between gap-2 p-4">
            {isUploadEnabled && (
              <>
                <Upload.Form
                  organizationId={organizationId}
                  onUpload={assets => form.setValue('assets', assets)}
                />
                <Upload.DropOverlay />
              </>
            )}
            <TemplateSelector onSelect={id => form.setValue('usecaseId', id)} />
            <Button
              className="ml-auto"
              disabled={!isValid || mutation.isPending || reachedLimit}
              type="submit"
            >
              {mutation.isPending ? (
                <ALogoSpinner className="size-4" />
              ) : (
                <Rocket className="size-4" />
              )}
              Build
            </Button>
          </div>
        </ABorderBox>
        <div className="mt-2 flex flex-wrap gap-2">
          {prompts.map(prompt => (
            <Button
              size="sm"
              variant="outline"
              type="button"
              key={prompt.name}
              onClick={() => {
                form.setValue('prompt', prompt.prompt(), {
                  shouldValidate: true,
                  shouldDirty: true,
                })
                form.setFocus('prompt')
              }}
            >
              <prompt.icon className="size-4" />
              {prompt.name}
            </Button>
          ))}
        </div>
      </form>
    </Upload.Provider>
  )
}

export function useFocusPrompt() {
  const focus = useCallback(() => {
    const promptElement = document.getElementById(TEXTAREA_ID)
    if (promptElement) {
      promptElement.focus()
    }
  }, [])

  return focus
}

const TEXTAREA_ID = 'project-prompt-textarea'
